import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CameraError } from '../utils/camera';
import './CameraCapture.css';

export interface CameraCaptureProps {
  /** Called on cancel / unrecoverable error. */
  onCancel: () => void;
  /** Called when the user taps "Folosește (N)" — hands all captured shots
   *  back in submission (capture) order. Replaces the old `onCapture`. */
  onDone: (shots: File[]) => void;
  /** Cap the number of shots in one session. Defaults to 10. */
  maxShots?: number;
  /** Existing partial captures (e.g. user reopens camera to add more). */
  initialShots?: File[];
}

const DEFAULT_MAX_SHOTS = 10;
const TOAST_DURATION_MS = 3500;

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

function buildFilename(captureNumber: number): string {
  const d = new Date();
  const date = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  return `camera-${date}-${time}-${captureNumber}.jpg`;
}

function classifyError(err: unknown): { code: CameraError['code']; message: string } {
  const e = err as { name?: string; message?: string; code?: CameraError['code'] } | undefined;
  if (e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
    return {
      code: 'PERMISSION_DENIED',
      message: 'Permisiunea pentru cameră este refuzată. Activează accesul la cameră din setări.',
    };
  }
  if (e && (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError')) {
    return { code: 'DEVICE_NOT_FOUND', message: 'Niciun dispozitiv de cameră detectat.' };
  }
  if (err instanceof CameraError) {
    return { code: err.code, message: err.message };
  }
  return { code: 'UNKNOWN', message: e?.message || 'Camera nu a putut fi inițializată.' };
}

const CameraCapture: React.FC<CameraCaptureProps> = ({
  onCancel,
  onDone,
  maxShots = DEFAULT_MAX_SHOTS,
  initialShots,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  // Independent monotonic counter for filename numbering — keeps incrementing
  // even after a shot is removed, so file names stay unique within a session.
  const captureCountRef = useRef<number>(initialShots ? initialShots.length : 0);

  // Stable object-URL cache keyed by File identity.
  const urlsRef = useRef<Map<File, string>>(new Map());

  // True once we've successfully captured at least one frame this session —
  // used to downgrade later init failures from fatal to toast.
  const hasReceivedFrameRef = useRef<boolean>(false);

  const toastTimerRef = useRef<number | null>(null);

  const [shots, setShots] = useState<File[]>(() => (initialShots ?? []).slice());
  const [reviewingIdx, setReviewingIdx] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState<boolean>(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState<boolean>(false);
  const [toast, setToast] = useState<string | null>(null);

  // ── Object URL helpers ───────────────────────────────────────────────
  const getThumbUrl = useCallback((file: File): string => {
    let url = urlsRef.current.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      urlsRef.current.set(file, url);
    }
    return url;
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimerRef.current != null) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, TOAST_DURATION_MS);
  }, []);

  // ── Camera stream lifecycle ──────────────────────────────────────────
  const stopCurrentStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      });
      streamRef.current = null;
    }
  }, []);

  const initializeCamera = useCallback(
    async (preferred: 'environment' | 'user' = 'environment') => {
      setLoading(true);
      setError(null);

      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        const msg = 'Camera API nu este suportat de acest browser.';
        if (hasReceivedFrameRef.current && shots.length > 0) {
          showToast(msg);
        } else {
          setError(msg);
        }
        setLoading(false);
        return;
      }

      stopCurrentStream();

      let stream: MediaStream | null = null;
      let resolvedFacing: 'environment' | 'user' = preferred;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: preferred,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
      } catch (primaryErr) {
        if (preferred === 'environment') {
          // Fall back to front-facing if rear is unavailable.
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: 'user',
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
              audio: false,
            });
            resolvedFacing = 'user';
          } catch (fallbackErr) {
            const info = classifyError(fallbackErr);
            if (hasReceivedFrameRef.current && shots.length > 0) {
              showToast(info.message);
            } else {
              setError(info.message);
            }
            setLoading(false);
            return;
          }
        } else {
          const info = classifyError(primaryErr);
          if (hasReceivedFrameRef.current && shots.length > 0) {
            showToast(info.message);
          } else {
            setError(info.message);
          }
          setLoading(false);
          return;
        }
      }

      streamRef.current = stream;
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
      }
      setFacingMode(resolvedFacing);

      // Probe how many cameras exist so we know whether to show the flip button.
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);
      } catch {
        setHasMultipleCameras(false);
      }

      setLoading(false);
    },
    [shots.length, showToast, stopCurrentStream]
  );

  // ── Mount / unmount ──────────────────────────────────────────────────
  useEffect(() => {
    initializeCamera('environment');
    return () => {
      stopCurrentStream();
      Array.from(urlsRef.current.values()).forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current.clear();
      if (toastTimerRef.current != null) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
    // initializeCamera intentionally not in deps — we only init once on mount;
    // toggleFacing calls initializeCamera explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop cached URLs for files that have been removed from state.
  useEffect(() => {
    const current = new Set(shots);
    for (const [file, url] of Array.from(urlsRef.current.entries())) {
      if (!current.has(file)) {
        URL.revokeObjectURL(url);
        urlsRef.current.delete(file);
      }
    }
  }, [shots]);

  // Auto-scroll the thumbnail strip to the latest capture.
  useEffect(() => {
    if (stripRef.current) {
      stripRef.current.scrollLeft = stripRef.current.scrollWidth;
    }
  }, [shots.length]);

  // ── Capture ──────────────────────────────────────────────────────────
  const handleCapture = useCallback(async () => {
    if (capturing || loading) return;
    if (shots.length >= maxShots) return;
    const video = videoRef.current;
    if (!video) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      showToast('Camera încă se inițializează. Încearcă din nou într-o clipă.');
      return;
    }

    setCapturing(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context indisponibil.');
      ctx.drawImage(video, 0, 0, vw, vh);

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9)
      );
      if (!blob) throw new Error('Nu s-a putut captura cadrul.');

      captureCountRef.current += 1;
      const filename = buildFilename(captureCountRef.current);
      const file = new File([blob], filename, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      hasReceivedFrameRef.current = true;
      setShots((prev) => [...prev, file]);
    } catch (err) {
      const info = classifyError(err);
      showToast(info.message);
    } finally {
      setCapturing(false);
    }
  }, [capturing, loading, maxShots, shots.length, showToast]);

  const handleRemove = useCallback((index: number) => {
    setShots((prev) => prev.filter((_, i) => i !== index));
    setReviewingIdx((cur) => {
      if (cur == null) return cur;
      if (cur === index) return null;
      if (cur > index) return cur - 1;
      return cur;
    });
  }, []);

  const handleRetake = useCallback(
    (index: number) => {
      handleRemove(index);
    },
    [handleRemove]
  );

  const handleToggleFacing = useCallback(async () => {
    const next: 'environment' | 'user' = facingMode === 'environment' ? 'user' : 'environment';
    await initializeCamera(next);
  }, [facingMode, initializeCamera]);

  const handleDone = useCallback(() => {
    if (shots.length === 0) return;
    stopCurrentStream();
    // Shots are stored in capture order — that's our submission order.
    onDone(shots.slice());
  }, [onDone, shots, stopCurrentStream]);

  const handleCancel = useCallback(() => {
    stopCurrentStream();
    onCancel();
  }, [onCancel, stopCurrentStream]);

  // Keyboard shortcuts (desktop QoL): Space = capture, Esc = exit preview.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (error) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.code === 'Space' && reviewingIdx === null) {
        e.preventDefault();
        void handleCapture();
      } else if (e.code === 'Escape' && reviewingIdx !== null) {
        e.preventDefault();
        setReviewingIdx(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleCapture, reviewingIdx, error]);

  const atMax = shots.length >= maxShots;

  // ── Render: fatal error state (only when we have no shots yet) ───────
  if (error && shots.length === 0) {
    return (
      <div className="camera-capture camera-capture-error">
        <div className="camera-capture-error-box">
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3>Eroare cameră</h3>
          <p>{error}</p>
          <div className="camera-capture-error-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => initializeCamera('environment')}
            >
              Încearcă din nou
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Anulează
            </button>
          </div>
        </div>
      </div>
    );
  }

  const reviewingFile = reviewingIdx != null ? shots[reviewingIdx] ?? null : null;

  return (
    <div className="camera-capture" role="dialog" aria-label="Captură cu camera">
      <div
        className="camera-capture-viewport"
        onClick={() => {
          if (!atMax) void handleCapture();
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="camera-capture-video"
          style={{ opacity: loading ? 0.4 : 1 }}
        />

        {loading && (
          <div className="camera-capture-loading" aria-live="polite">
            <div className="camera-capture-spinner" />
            <span>Se încarcă camera...</span>
          </div>
        )}

        <div className="camera-capture-counter" aria-live="polite">
          {shots.length} / {maxShots}
        </div>

        {atMax && (
          <div className="camera-capture-hint" role="status">
            Limită maximă de fotografii atinsă
          </div>
        )}

        <div className="camera-capture-rotate-hint" aria-hidden="true">
          Rotește telefonul vertical pentru a folosi camera
        </div>
      </div>

      {shots.length > 0 && (
        <div
          className="camera-capture-strip"
          ref={stripRef}
          onClick={(e) => e.stopPropagation()}
          aria-label="Capturi"
        >
          {shots.map((file, idx) => (
            <div className="camera-capture-thumb" key={`${file.name}-${idx}`}>
              <button
                type="button"
                className="camera-capture-thumb-btn"
                onClick={() => setReviewingIdx(idx)}
                title={`Previzualizează captura ${idx + 1}`}
                aria-label={`Previzualizează captura ${idx + 1}`}
              >
                <img src={getThumbUrl(file)} alt={`Captură ${idx + 1}`} />
              </button>
              <button
                type="button"
                className="camera-capture-thumb-remove"
                aria-label={`Șterge captura ${idx + 1}`}
                title="Șterge"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(idx);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="camera-capture-controls" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="camera-capture-side-btn"
          onClick={handleCancel}
          title="Anulează"
          aria-label="Anulează"
        >
          ✕
        </button>

        <button
          type="button"
          className={`camera-capture-shutter${atMax ? ' is-disabled' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            void handleCapture();
          }}
          disabled={capturing || loading || atMax}
          title="Fotografiază"
          aria-label="Fotografiază"
        >
          <span className="camera-capture-shutter-inner" />
        </button>

        {hasMultipleCameras ? (
          <button
            type="button"
            className="camera-capture-side-btn"
            onClick={handleToggleFacing}
            disabled={loading || capturing}
            title="Inversează camera"
            aria-label="Inversează camera"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1 4v6h6M23 20v-6h-6" />
              <path d="M20.38 13.67A8 8 0 0 0 3.62 10.33M2 21v-5h5M22 3v5h-5" />
            </svg>
          </button>
        ) : (
          <span className="camera-capture-side-spacer" aria-hidden="true" />
        )}
      </div>

      {shots.length > 0 && (
        <div className="camera-capture-review" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void handleCapture()}
            disabled={atMax || capturing || loading}
          >
            + Adaugă încă o poză
          </button>
          <button type="button" className="btn btn-primary" onClick={handleDone}>
            Folosește ({shots.length}) ▶
          </button>
        </div>
      )}

      {reviewingFile && (
        <div
          className="camera-capture-preview-overlay"
          onClick={() => setReviewingIdx(null)}
          role="dialog"
          aria-label="Previzualizare captură"
        >
          <div className="camera-capture-preview-inner" onClick={(e) => e.stopPropagation()}>
            <img src={getThumbUrl(reviewingFile)} alt="Previzualizare captură" />
            <div className="camera-capture-preview-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setReviewingIdx(null)}
              >
                Înapoi
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => reviewingIdx != null && handleRetake(reviewingIdx)}
              >
                Refotografiază
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="camera-capture-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
};

export default CameraCapture;
